import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { FORMA_PAGAMENTO_ROTULO, formatarDataHora, formatarDataHoraSegundos, formatarReais } from "@caixaflow/shared";
import { CHAVE_CAIXA_ATUAL } from "../../api/caixa";
import { api, ErroApi } from "../../api/cliente";
import { useSessao } from "../../api/sessao";
import type { CaixaDetalhe } from "../../api/tipos";
import { IndicadoresCaixa, ItensVendidos, TabelaFechamento } from "../../components/Caixa";
import { Campo } from "../../components/Campos";
import { Carregando, FalhaAoCarregar, mensagemDeErro } from "../../components/Estados";
import { Modal } from "../../components/Modal";
import { ModalVenda } from "../../components/ModalVenda";
import { ModalOferta } from "../../components/Ofertas";
import { useToast } from "../../components/Toast";

export function PaginaDetalheCaixa() {
  const { id = "" } = useParams();
  const { data: sessao } = useSessao();
  const caixa = useQuery({ queryKey: ["caixa", id], queryFn: () => api<CaixaDetalhe>(`/caixas/${id}`) });
  const [reabrindo, setReabrindo] = useState(false);
  const [vendaAberta, setVendaAberta] = useState<string | null>(null);
  const [ofertaAberta, setOfertaAberta] = useState<string | null>(null);

  if (caixa.isPending) return <Carregando />;
  if (caixa.error) return <FalhaAoCarregar erro={caixa.error} tentarDeNovo={() => caixa.refetch()} />;
  const c = caixa.data;
  const souAdmin = sessao?.usuario.perfil === "ADMIN";
  const meuAberto = c.status === "ABERTO" && c.operador.id === sessao?.usuario.id;
  const fechamento = c.fechamentoVigente;

  return (
    <>
      <div className="linha">
        <div>
          <h3>
            Caixa #{c.numero}{" "}
            <span className={`chip ${c.status === "ABERTO" ? "ok" : "neutro"}`}>{c.status === "ABERTO" ? "Aberto" : "Fechado"}</span>
            {c.origem === "IMPORTACAO" && <span className="chip alerta" style={{ marginLeft: 6 }}>Importado</span>}
          </h3>
          <span className="mudo pequeno">
            {c.evento.nome} · {c.operador.nome}
          </span>
        </div>
        <div className="linha-esq">
          {meuAberto && (
            <Link className="btn pequeno primario" to="/caixa">
              Ir para o caixa
            </Link>
          )}
          {souAdmin && c.status === "FECHADO" && c.origem === "PDV" && c.evento.status !== "ENCERRADO" && (
            <button className="btn pequeno fantasma" onClick={() => setReabrindo(true)}>
              Reabrir caixa
            </button>
          )}
          <a className="btn pequeno primario" href={`/api/caixas/${c.id}/comprovante.pdf`} download>
            Exportar PDF do caixa
          </a>
          <Link className="btn pequeno fantasma" to={`/vendas?caixa=${c.id}&status=TODAS`}>
            Vendas deste caixa
          </Link>
        </div>
      </div>

      <IndicadoresCaixa totais={c.resumo.totais} />

      <section className="card pad">
        <h2>Abertura</h2>
        <dl className="dados">
          <dt>Data e hora</dt>
          <dd>{formatarDataHora(c.abertoEm)}</dd>
          <dt>Fundo de troco</dt>
          <dd>{formatarReais(c.fundoTrocoCentavos)}</dd>
          <dt>Observações</dt>
          <dd>{c.obsAbertura || "—"}</dd>
        </dl>
      </section>

      {fechamento ? (
        <section>
          <h2>Fechamento</h2>
          <div className="card pad" style={{ marginBottom: 10 }}>
            <dl className="dados">
              <dt>Data e hora</dt>
              <dd>{formatarDataHora(fechamento.fechadoEm)}</dd>
              <dt>Fechado por</dt>
              <dd>{fechamento.fechadoPor.nome}</dd>
              <dt>Observações</dt>
              <dd>{fechamento.observacoes || "—"}</dd>
            </dl>
          </div>
          <TabelaFechamento linhas={fechamento.linhas} />
          {c.fechamentoRecalculado && (
            <div className="aviso alerta" style={{ marginTop: 10 }}>
              O esperado foi recalculado depois do fechamento, por cancelamento ou ajuste de venda. O contado é o da conferência
              original.
            </div>
          )}
        </section>
      ) : (
        <section>
          <h2>Vendido por forma de pagamento</h2>
          <div className="card tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Forma</th>
                  <th className="r">Vendas</th>
                  <th className="r">Vendido</th>
                  <th className="r">Esperado</th>
                </tr>
              </thead>
              <tbody>
                {c.resumo.porForma.map((l) => (
                  <tr key={l.formaPagamento}>
                    <td>{FORMA_PAGAMENTO_ROTULO[l.formaPagamento]}</td>
                    <td className="r">{l.quantidadeVendas}</td>
                    <td className="r">{formatarReais(l.vendidoCentavos)}</td>
                    <td className="r">{formatarReais(l.esperadoCentavos)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <h2>Itens vendidos</h2>
        <ItensVendidos itens={c.resumo.itens} />
      </section>

      <section>
        <h2>Vendas ({c.vendas.length})</h2>
        {c.vendas.length === 0 ? (
          <div className="card vazio">Nenhuma venda neste caixa.</div>
        ) : (
          <div className="card">
            {c.vendas.map((v) => (
              <div key={v.id} className={`venda-linha${v.status === "CANCELADA" ? " cancelada" : ""}`}>
                <span className="t">{formatarDataHoraSegundos(v.criadaEm)}</span>
                <span className="d">
                  #{v.numero} · {v.itens.map((i) => `${i.quantidade}× ${i.nomeProduto}`).join(", ")}
                  <span className="chip">{FORMA_PAGAMENTO_ROTULO[v.formaPagamento]}</span>
                  {v.status === "CANCELADA" && <span className="chip perigo">cancelada</span>}
                  {v.ajustes > 0 && <span className="chip alerta">ajustada</span>}
                  {v.formaPagamento === "DINHEIRO" && v.recebidoCentavos !== null && (
                    <div className="mudo pequeno">
                      recebido {formatarReais(v.recebidoCentavos)} · troco {formatarReais(v.trocoCentavos ?? 0)}
                    </div>
                  )}
                </span>
                <span className="v">{formatarReais(v.totalCentavos)}</span>
                <button className="link-botao" onClick={() => setVendaAberta(v.id)} aria-label={`Ver venda #${v.numero}`}>
                  Ver
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2>Vendas canceladas</h2>
        {c.canceladas.length === 0 ? (
          <div className="card vazio">Nenhuma venda cancelada.</div>
        ) : (
          <div className="card tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Venda</th>
                  <th>Cancelada em</th>
                  <th className="r">Valor</th>
                  <th>Motivo</th>
                  <th>Autorizado por</th>
                </tr>
              </thead>
              <tbody>
                {c.canceladas.map((v) => (
                  <tr key={v.id}>
                    <td className="quebra">
                      #{v.numero} · {v.itens.map((i) => `${i.quantidade}× ${i.nomeProduto}`).join(", ")}
                    </td>
                    <td className="mudo">{formatarDataHora(v.canceladaEm)}</td>
                    <td className="r">{formatarReais(v.totalCentavos)}</td>
                    <td className="quebra">{v.motivo}</td>
                    <td>{v.autorizadoPor.nome}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {c.ajustes.length > 0 && (
        <section>
          <h2>Ajustes de vendas</h2>
          <div className="card">
            {c.ajustes.map((a) => (
              <div key={a.id} className="venda-linha" style={{ gridTemplateColumns: "1fr" }}>
                <span className="pequeno">
                  <b>
                    Venda #{a.venda.numero} · {formatarDataHora(a.em)}
                  </b>{" "}
                  · {a.motivo} · autorizado por {a.autorizadoPor.nome}
                  {a.caixaFechado && " · depois do fechamento"}
                  <div className="mudo">
                    Antes: {FORMA_PAGAMENTO_ROTULO[a.antes.formaPagamento]} · {formatarReais(a.antes.totalCentavos)} · depois:{" "}
                    {FORMA_PAGAMENTO_ROTULO[a.depois.formaPagamento]} · {formatarReais(a.depois.totalCentavos)}
                  </div>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2>Ofertas</h2>
        {c.ofertas.length === 0 ? (
          <div className="card vazio">Nenhuma oferta neste caixa.</div>
        ) : (
          <div className="card">
            {c.ofertas.map((o) => (
              <div key={o.id} className={`venda-linha${o.status === "CANCELADA" ? " cancelada" : ""}`}>
                <span className="t">{formatarDataHora(o.criadaEm)}</span>
                <span className="d">
                  #{o.numero} · {o.itens.map((i) => `${i.quantidade}× ${i.nomeProduto}`).join(", ")}
                  {o.status === "CANCELADA" && <span className="chip perigo">cancelada</span>}
                  <div className="mudo pequeno">
                    Pedido de {o.solicitadoPor} · autorizada por {o.autorizadaPor.nome}
                  </div>
                </span>
                <span className="v mudo">{formatarReais(o.valorReferenciaCentavos)}</span>
                <button className="link-botao" onClick={() => setOfertaAberta(o.id)} aria-label={`Ver oferta #${o.numero}`}>
                  Ver
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="mudo pequeno">Ofertas são itens entregues sem pagamento: não entram no vendido nem no esperado do caixa.</p>
      </section>

      {c.reaberturas.length > 0 && (
        <section>
          <h2>Reaberturas</h2>
          <div className="card">
            {c.reaberturas.map((r) => (
              <div key={r.id} className="venda-linha" style={{ gridTemplateColumns: "1fr" }}>
                <span className="pequeno">
                  Fechado em {formatarDataHora(r.fechadoEm)} por {r.fechadoPor.nome}; reaberto em{" "}
                  {r.reabertoEm && formatarDataHora(r.reabertoEm)} por {r.reabertoPor?.nome}. Motivo: {r.motivoReabertura}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <ModalVenda vendaId={vendaAberta} onFechar={() => setVendaAberta(null)} />
      <ModalOferta ofertaId={ofertaAberta} onFechar={() => setOfertaAberta(null)} />
      <Modal aberto={reabrindo} titulo="Reabrir caixa" onFechar={() => setReabrindo(false)}>
        {reabrindo && <FormReabrir caixa={c} onPronto={() => setReabrindo(false)} />}
      </Modal>
    </>
  );
}

function FormReabrir({ caixa, onPronto }: { caixa: CaixaDetalhe; onPronto: () => void }) {
  const [motivo, setMotivo] = useState("");
  const qc = useQueryClient();
  const toast = useToast();
  const reabrir = useMutation({
    mutationFn: () => api<CaixaDetalhe>(`/caixas/${caixa.id}/reabrir`, "POST", { motivo }),
    onSuccess: (c) => {
      qc.setQueryData(["caixa", c.id], c);
      // O caixa reaberto pode ser o do próprio ADMIN: recarrega o "caixa atual".
      qc.invalidateQueries({ queryKey: CHAVE_CAIXA_ATUAL });
      qc.invalidateQueries({ queryKey: ["caixas"] });
      toast(`Caixa #${c.numero} reaberto`);
      onPronto();
    },
  });
  const erro = reabrir.error instanceof ErroApi ? reabrir.error : null;
  return (
    <form
      className="form"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        reabrir.mutate();
      }}
    >
      <p style={{ margin: 0 }}>
        O caixa #{caixa.numero} de <b>{caixa.operador.nome}</b> volta a aceitar vendas e cancelamentos. O fechamento atual
        fica guardado no histórico.
      </p>
      {reabrir.error && !erro?.campos.length && <div className="aviso erro">{mensagemDeErro(reabrir.error)}</div>}
      <Campo rotulo="Motivo" erro={erro?.doCampo("motivo")}>
        <input required minLength={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: contagem errada" />
      </Campo>
      <div className="modal-acoes">
        <button type="button" className="btn fantasma" onClick={onPronto}>
          Voltar
        </button>
        <button type="submit" className="btn primario" disabled={reabrir.isPending}>
          {reabrir.isPending ? "Reabrindo…" : "Reabrir"}
        </button>
      </div>
    </form>
  );
}
